import { useEffect, useRef } from "react";

export * from "./keys";

/**
 * В оболочке ли мы.
 *
 * Признак — наличие `window.airelShell`: его кладёт preload Electron и только
 * там. В вебе его нет, и десктопные ветки не включаются. (Отладочный
 * `window.airel` для этого не годится — он появляется и в браузере.)
 */
export function isDesktop(): boolean {
  return typeof window !== "undefined" && !!window.airelShell;
}

export const windowControls = {
  minimize: async () => window.airelShell?.window.minimize(),
  toggleMaximize: async () => window.airelShell?.window.toggleMaximize(),
  close: async () => window.airelShell?.window.close(),
  show: async () => window.airelShell?.window.show(),
};

/** Что мешает безопасно закрыть приложение прямо сейчас. */
export interface RestartBlocker {
  id: string;
  title: string;
  detail: string;
  /** `wait` нельзя прервать; `confirm` updater умеет аккуратно завершить. */
  kind: "wait" | "confirm";
  prepare?: (signal?: AbortSignal) => Promise<void> | void;
}

type BlockerReader = () => RestartBlocker | null;
type Preparation = (signal?: AbortSignal) => Promise<void> | void;

const blockers = new Set<BlockerReader>();
const preparations = new Set<Preparation>();
const listeners = new Set<() => void>();
let revision = 0;
let restartIntent: symbol | null = null;

export class RestartInProgressError extends Error {
  constructor() {
    super("Airel уже устанавливает обновление. Дождитесь перезапуска");
    this.name = "RestartInProgressError";
  }
}

function changed() {
  revision += 1;
  for (const listener of listeners) listener();
}

export function subscribeRestartLifecycle(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function restartLifecycleVersion(): number {
  return revision;
}

export function currentRestartBlockers(): RestartBlocker[] {
  const found = new Map<string, RestartBlocker>();

  for (const read of blockers) {
    const blocker = read();
    if (blocker) found.set(blocker.id, blocker);
  }

  return [...found.values()];
}

/** Не даёт новому изменяющему действию вклиниться перед запуском installer. */
export function assertCanStartRestartSensitiveOperation(): void {
  if (restartIntent) throw new RestartInProgressError();
}

export function restartHandoffInProgress(): boolean {
  return restartIntent !== null;
}

/**
 * Закрывает окно между последней проверкой blockers и native handoff.
 * Возвращённую функцию надо вызвать, только если приложение осталось жить.
 */
export function acquireRestartHandoff(): () => void {
  assertCanStartRestartSensitiveOperation();
  const own = Symbol("restart-handoff");
  restartIntent = own;
  changed();

  return () => {
    if (restartIntent !== own) return;
    restartIntent = null;
    changed();
  };
}

export function registerRestartBlocker(read: BlockerReader): () => void {
  blockers.add(read);
  changed();
  return () => {
    blockers.delete(read);
    changed();
  };
}

/** Держит асинхронную работу в списке даже после размонтирования её экрана. */
export function holdRestartBlocker(blocker: RestartBlocker): () => void {
  assertCanStartRestartSensitiveOperation();
  return registerRestartBlocker(() => blocker);
}

export function registerRestartPreparation(task: Preparation): () => void {
  preparations.add(task);
  return () => preparations.delete(task);
}

/** Задача, которую надо выполнить перед любым разрешённым перезапуском. */
export function useRestartPreparation(task: Preparation): void {
  const latest = useRef(task);
  latest.current = task;

  useEffect(() => {
    const run: Preparation = (signal) => latest.current(signal);
    return registerRestartPreparation(run);
  }, []);
}

/** Регистрирует живое состояние, поэтому один hook годится для меняющегося звонка или загрузки. */
export function useRestartBlocker(blocker: RestartBlocker | null): void {
  const latest = useRef(blocker);
  latest.current = blocker;

  useEffect(() => {
    const read: BlockerReader = () => latest.current;
    return registerRestartBlocker(read);
  }, []);

  useEffect(changed, [blocker?.id, blocker?.kind, blocker?.title, blocker?.detail]);
}

/** Сначала фиксируем локальные данные, затем завершаем подтверждённые действия. */
export async function prepareForRestart(
  selected: RestartBlocker[],
  signal?: AbortSignal,
): Promise<void> {
  for (const prepare of preparations) {
    signal?.throwIfAborted();
    await prepare(signal);
  }
  for (const blocker of selected) {
    signal?.throwIfAborted();
    await blocker.prepare?.(signal);
  }
}

const DRAFT_PREFIX = "airel.chat-draft.v1";
const DRAFT_LIMIT = 20_000;

function draftKey(userId: string, place: string): string {
  return `${DRAFT_PREFIX}:${userId}:${place}`;
}

export function readDraft(userId: string, place: string): string {
  try {
    return localStorage.getItem(draftKey(userId, place)) ?? "";
  } catch {
    return "";
  }
}

export function writeDraft(userId: string, place: string, value: string): void {
  try {
    const storageKey = draftKey(userId, place);
    if (value) localStorage.setItem(storageKey, value.slice(0, DRAFT_LIMIT));
    else localStorage.removeItem(storageKey);
  } catch {
    // Переполненное или запрещённое хранилище не должно ломать само поле ввода.
  }
}
